/**
 * The my user state selectors
 */

import { createSelector } from 'reselect';

import { FULL_ACCESS, InternalAdminRole } from 'constants/internal-admin-roles';
import { selectCurrentEnterpriseId } from 'store/modules/enterprise/selectors';
import { ApplicationRootState } from 'store/types';
import { ProtectedResource, ResourceAction } from 'types/action.enum';
import Enterprise from 'types/models/enterprise';
import Membership, { UserRole } from 'types/models/membership';
import { checkIsInternalAdmin } from 'utils/AccessControl/check-is-internal-admin';
import createDeepEqualSelector from 'utils/create-deep-equal-selector';

const selectMyUserState = (state: ApplicationRootState) => state.myUser;

const selectMyUserMembershipRole = createSelector(
  [selectMyUserState, selectCurrentEnterpriseId],
  (myUserState, enterpriseId) => {
    const currentMembership: Membership | null | undefined =
      myUserState?.memberships &&
      myUserState.memberships.find(
        (membership) => membership.enterpriseId === enterpriseId
      );
    return currentMembership && currentMembership.role;
  }
);

const selectMyUser = createDeepEqualSelector(
  [selectMyUserState, selectMyUserMembershipRole],
  (myUserState, membershipRole) => {
    if (!myUserState) {
      return null;
    }

    const { firstName, lastName } = myUserState;

    return {
      ...myUserState,
      fullName: `${firstName} ${lastName}`,
      role: membershipRole
    };
  }
);

const selectMyUserExists = createSelector(
  selectMyUser,
  // Check myUser !== undefined is not enough. When users signed in, we store
  // user id (from Auth0) alone into myUser, then init the requestMyUser. If
  // we manage to get the complete user info, there must be a username.
  (myUser) => myUser && !!myUser.username
);

const selectMyUserMemberships = createSelector(
  selectMyUser,
  (myUser) => myUser && myUser.memberships
);

/**
 * Returns the list of country codes the user can access if they are an internal sales admin with restricted access.
 * Returns FULL_ACCESS ('*') if the user is not an internal sales admin with restrictions
 * (including all other admin roles).
 * Returns an empty array if the user is not an internal admin.
 */
const selectMyUserInternalAdminCountryAccess = createSelector(
  selectMyUser,
  (myUser) => {
    if (!myUser || myUser.adminRole === InternalAdminRole._NONE) {
      return [];
    }
    if (
      myUser.adminRole === InternalAdminRole.SALES_ADMIN &&
      myUser.permissions.accessCountries !== FULL_ACCESS
    ) {
      return myUser.permissions.accessCountries.map((country) => country.code);
    }
    return FULL_ACCESS;
  }
);

const selectMyUserIsInternalAdmin = createSelector(
  selectMyUser,
  (myUser) => !!myUser && checkIsInternalAdmin(myUser)
);

const selectMyUserIsAdmin = createSelector(
  selectMyUser,
  (myUser) => myUser && myUser.role === UserRole.ADMIN
);

const selectMyUserIsEmailVerified = createSelector(
  selectMyUser,
  (myUser) => myUser && myUser.isEmailVerified
);

const makeSelectMyUserMembership = (enterpriseId: number) => {
  return createSelector(
    selectMyUserMemberships,
    (myUserMemberships) =>
      myUserMemberships &&
      myUserMemberships.find(
        (element: Membership) => element.enterpriseId === enterpriseId
      )
  );
};

const selectMyUserMembershipsEnterprises = createSelector(
  selectMyUserMemberships,
  (myUserMemberships) =>
    myUserMemberships
      ? myUserMemberships
        .map((membership) => membership.enterprise)
        // Filter falsy values.
        .filter<Enterprise>((e): e is Enterprise => !!e)
      : []
);

const makeSelectMyUserMembershipsEnterprisesByRoles = (roles: UserRole[]) =>
  createSelector(selectMyUserMemberships, (myUserMemberships) =>
    myUserMemberships
      ? myUserMemberships
        .filter((m) => roles.includes(m.role))
        .map((m) => m.enterprise)
        // Filter falsy values.
        .filter<Enterprise>((e): e is Enterprise => !!e)
      : []
  );

const selectMyUserMembershipsEnterpriseIds = createSelector(
  selectMyUserMembershipsEnterprises,
  (myUserMembershipsEnterprises) =>
    myUserMembershipsEnterprises.map(({ id }) => id)
);

const selectHasMultipleMemberships = createSelector(
  selectMyUserMembershipsEnterpriseIds,
  (myUserMembershipsEnterprises) => myUserMembershipsEnterprises.length > 1
);

const selectHasOnlyOneMembership = createSelector(
  selectMyUserMembershipsEnterpriseIds,
  (myUserMembershipsEnterprises) => myUserMembershipsEnterprises.length === 1
);

/**
 * Checks if the user's enterprise membership role matches any of the specified roles.
 * Does not grant roles implicitly to internal admins.
 */
const makeSelectUserRoleGranted = (userRoles: UserRole[]) => createSelector(
  selectMyUserMembershipRole,
  (role) => !!role && userRoles.includes(role)
);

const makeSelectIsMyUser = (id: number) =>
  createSelector(selectMyUser, (myUser) => !!myUser && myUser.id === id);

const makeSelectMyUserHasInternalPermission = ({ resource, action }: {
  resource: ProtectedResource;
  action: ResourceAction;
}) =>
  createSelector(
    selectMyUser,
    (myUser) => {
      const authorizedActions = myUser?.internalAccessPolicy?.authorizedActions;
      if (!authorizedActions) return false;
      return Boolean(authorizedActions[resource]?.includes(action));
    }
  );

export {
  makeSelectIsMyUser,
  makeSelectMyUserHasInternalPermission,
  makeSelectMyUserMembership,
  makeSelectMyUserMembershipsEnterprisesByRoles,
  makeSelectUserRoleGranted,
  selectHasMultipleMemberships,
  selectHasOnlyOneMembership,
  selectMyUser,
  selectMyUserExists,
  selectMyUserInternalAdminCountryAccess,
  selectMyUserIsAdmin,
  selectMyUserIsEmailVerified,
  selectMyUserIsInternalAdmin,
  selectMyUserMembershipRole,
  selectMyUserMemberships,
  selectMyUserMembershipsEnterpriseIds,
  selectMyUserMembershipsEnterprises
};
